import { useMemo } from 'react';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { useAuth } from '@/contexts/AuthContext';
import { addDays, isSameDay, format, startOfDay, endOfDay } from 'date-fns';

export const useSmartDateSelection = () => {
  const { user } = useAuth();
  const { config: agendaConfig } = useAgendaConfig();

  return useMemo(() => {
    if (!user || !agendaConfig) {
      return new Date();
    }

    const today = new Date();
    const now = new Date();
    const workingDays = agendaConfig.working_days || ['mon', 'tue', 'wed', 'thu', 'fri'];
    
    // Check if today is a working day
    const todayDayName = today.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
    const isTodayWorking = workingDays.includes(todayDayName);
    
    if (!isTodayWorking) {
      // If today is not a working day, find the next working day
      let nextWorkingDay = today;
      let attempts = 0;
      const maxAttempts = 7; // Prevent infinite loop
      
      while (attempts < maxAttempts) {
        nextWorkingDay = addDays(nextWorkingDay, 1);
        const dayName = nextWorkingDay.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
        
        if (workingDays.includes(dayName)) {
          return nextWorkingDay;
        }
        
        attempts++;
      }
    }
    
    // If today is a working day, check if there are still available slots
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    
    // Get today's working hours
    const todayWorkingHours = agendaConfig.working_hours?.[todayDayName];
    
    // Check if current time is past the close time for today
    if (todayWorkingHours?.end) {
      // Convert end time to 24-hour format if needed
      const convertTimeTo24Hour = (timeString: string): string => {
        if (!timeString) return '00:00';
        
        // If already in 24-hour format (no AM/PM), return as is
        if (!timeString.includes('AM') && !timeString.includes('PM')) {
          const parts = timeString.split(':');
          if (parts.length === 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          }
          return '00:00';
        }
        
        // Parse AM/PM format
        const [time, period] = timeString.split(' ');
        let [hours, minutes] = time.split(':').map(Number);
        
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };
      
      const closeTime = convertTimeTo24Hour(todayWorkingHours.end);
      
      // If current time is past or at closing time, find next working day
      if (currentTimeString >= closeTime) {
        // Find next working day
        let nextWorkingDay = today;
        let attempts = 0;
        const maxAttempts = 7;
        
        while (attempts < maxAttempts) {
          nextWorkingDay = addDays(nextWorkingDay, 1);
          const dayName = nextWorkingDay.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
          
          if (workingDays.includes(dayName)) {
            return nextWorkingDay;
          }
          
          attempts++;
        }
      }
    }
    
    // If we're still within working hours today, return today
    return today;
  }, [user, agendaConfig]);
};
